import {
  Backend,
  Options,
  Event,
  Client,
  Integration,
  IntegrationClass,
} from "@jupiter/types";
import { Logger } from "@jupiter/utils";
import { BackendClass } from "./backend";
import { IntegrationIndex, setupIntegrations } from "./integration";

export abstract class BaseClient<B extends Backend, O extends Options>
  implements Client<O> {
  protected readonly _backend: B;
  protected readonly _options: O;

  protected _integrations: IntegrationIndex = {};

  constructor(backendClass: BackendClass<B, O>, options: O) {
    this._backend = new backendClass(options);
    this._options = options;
  }

  track(event: Event) {
    return this._processEvent(event);
  }

  /**
   * @inheritDoc
   */
  public getIntegration<T extends Integration>(
    integration: IntegrationClass<T>
  ): T | null {
    try {
      return (this._integrations[integration.id] as T) || null;
    } catch (_oO) {
      Logger.warn(
        `Cannot retrieve integration ${integration.id} from the current Client`
      );
      return null;
    }
  }

  setupIntegrations(): void {
    this._integrations = setupIntegrations(this._options);
  }

  /**
   * @inheritDoc
   */
  public getOptions(): O {
    return this._options;
  }

  /** Returns the current backend. */
  protected _getBackend(): B {
    return this._backend;
  }

  /**
   * Tells the backend to send this event
   * @param event The event to send
   */
  protected _sendEvent(event: Event) {
    return this._getBackend().sendEvent(event);
  }

  protected _prepareEvent(event: Event): PromiseLike<Event> {
    return Promise.resolve(event);
  }

  protected _processEvent(event: Event) {
    return this._prepareEvent(event).then((prepared) =>
      this._sendEvent(prepared)
    );
  }
}
